import { inject, Injectable } from '@angular/core';
import { Customer } from '../models/customer.model';
import {
  addDoc,
  collection,
  doc, documentId,
  Firestore,
  getDoc, getDocs,
  limit as firestoreLimit,
  Query,
  query,
  updateDoc, where
} from '@angular/fire/firestore';
import { ObjectHelper } from "../helpers/object.helper";
import {CustomerInvitation} from "../models/customer-invitation.model";
import { Functions, httpsCallable, HttpsCallableResult } from "@angular/fire/functions";
import { CoverageResult } from "../models/coverage-result.model";

@Injectable({
  providedIn: 'root',
})
export class CustomerService {

  private firestore = inject(Firestore);
  private functions = inject(Functions);

  /**
   * Subscribe to customers collection changes
   * @param organizationId organizationId
   * @returns Customers subscription
   */
  public list(organizationId: string): Query<Customer> {
    return query(
      collection(this.firestore, 'customers'),
      where('organizationId', '==', organizationId)
    ).withConverter(Customer.converter);
  }

  /**
   * Customers whose name starts with a text, for a search: the text as typed and with its first letter in capitals,
   * Firestore comparing names letter case included
   * @param organizationId Organization id
   * @param text Start of the name
   * @param limit Most customers returned for each spelling
   * @returns Customers found
   */
  async searchByName(organizationId: string, text: string, limit = 20): Promise<Customer[]> {
    const typed = text.trim();
    const spellings = [...new Set([typed, typed.charAt(0).toUpperCase() + typed.slice(1), typed.toUpperCase()])];
    const found = new Map<string, Customer>();
    for (const spelling of spellings.filter(Boolean)) {
      const snapshot = await getDocs(query(collection(this.firestore, 'customers'),
        where('organizationId', '==', organizationId),
        where('name', '>=', spelling),
        where('name', '<=', `${spelling}\uf8ff`),
        firestoreLimit(limit)).withConverter(Customer.converter));
      snapshot.docs.forEach(document => found.set(document.id, document.data()));
    }
    return [...found.values()];
  }

  /**
   * Get a customer by id
   * @param id The customer id
   * @returns The customer or null
   */
  async get(id: string): Promise<Customer | null> {
    const ref = doc(this.firestore, 'customers', id)
      .withConverter(Customer.converter);
    return getDoc(ref).then(doc => doc.data());
  }

  /**
   * Create a customer
   * @param customer The customer to save
   * @returns Id of the created machine
   */
  async create(customer: Customer): Promise<string> {
    const ref = collection(this.firestore, 'customers').withConverter(Customer.converter);
    const created = await addDoc(ref, customer);
    return created.id;
  }

  /**
   * Update a customer (partial patch — fields omitted from the payload are left unchanged).
   * @param id The customer id to update
   * @param customer The customer fields to save (e.g. form value without siret/coverage)
   * @returns Resolves when the Firestore document has been updated
   */
  async update(id: string, customer: Partial<Customer>): Promise<void> {
    const ref = doc(this.firestore, 'customers', id).withConverter(Customer.converter);
    await updateDoc(ref, ObjectHelper.anonymize(customer));
  }

  /**
   * Check if an erpId is used
   * @param organizationId The organizationId id
   * @param erpId Business id to check
   * @returns True if erp id is already used
   */
  async checkErpId(organizationId: string, erpId: string): Promise<boolean> {
    const q = query(collection(this.firestore, 'customers'),
      where('erpId', '==', erpId), where('organizationId', '==', organizationId))
      .withConverter(Customer.converter);
    const get = await getDocs(q);
    return !get.empty;
  }

  /**
   * Find all connected user customer accounts
   * @param organizationId The shop organization id
   * @param customerIds The user customer ids
   * @returns The customers
   */
  async userCustomersAccounts(organizationId: string, customerIds: string[]): Promise<Customer[]> {

    const batchSize = 10;
    const batches: string[][] = [];

    // Split customerIds into batches of 10
    for (let i = 0; i < customerIds.length; i += batchSize) {
      batches.push(customerIds.slice(i, i + batchSize));
    }

    // Prepare all queries
    const queries: Query[] = batches.map(batch =>
      query(
        collection(this.firestore, 'customers'),
        where('organizationId', '==', organizationId), // Filter by organizationId
        where(documentId(), 'in', batch) // Filter by batched document IDs
      ).withConverter(Customer.converter)
    );

    // Execute all queries and merge results
    const results = await Promise.all(
      queries.map(q => getDocs(q))
    );

    // Collect all documents from the batches
    const customers: Customer[] = [];
    results.forEach(querySnapshot => {
      querySnapshot.forEach(doc => {
        if (doc.exists()) {
          customers.push(doc.data() as Customer);
        }
      });
    });
    return customers
  }

  /**
   * Find all connected user customer accounts
   * @param customerIds The user customer ids
   * @returns The customers
   */
  async userCustomers(customerIds: string[]): Promise<Customer[]> {

    const batchSize = 10;
    const batches: string[][] = [];

    // Split customerIds into batches of 10
    for (let i = 0; i < customerIds.length; i += batchSize) {
      batches.push(customerIds.slice(i, i + batchSize));
    }

    // Prepare all queries
    const queries: Query[] = batches.map(batch =>
      query(
        collection(this.firestore, 'customers'),
        where(documentId(), 'in', batch) // Filter by batched document IDs
      ).withConverter(Customer.converter)
    );

    // Execute all queries and merge results
    const results = await Promise.all(
      queries.map(q => getDocs(q))
    );

    // Collect all documents from the batches
    const customers: Customer[] = [];
    results.forEach(querySnapshot => {
      querySnapshot.forEach(doc => {
        if (doc.exists()) {
          customers.push(doc.data() as Customer);
        }
      });
    });
    return customers
  }


  /**
   * List customer invitation of companies
   * @param customerId The customer id that has send the invitation
   * @returns The organizations
   */
  public async listCustomerInvitations(customerId: string): Promise<CustomerInvitation[]> {
    const q = query(collection(this.firestore, 'invitation-customer'),
      where('customerId', '==', customerId)
    )
      .withConverter(CustomerInvitation.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty ? [] : snapshot.docs.map(doc => doc.data())
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Manually re-trigger a Coface coverage check/ECL request for a customer (root users only)
   * @param customerId - The customer id to evaluate coverage for
   * @param amount - The order amount to check coverage against
   * @returns A promise that resolves with the coverage evaluation result
   */
  requestCoverage(customerId: string, amount: number): Promise<HttpsCallableResult<CoverageResult>> {
    return httpsCallable<{ customerId: string, amount: number }, CoverageResult>(
      this.functions, 'coface_request_coverage_gen2')({ customerId, amount });
  }
}
